import React from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { ParsedUrlQuery } from 'querystring';
import { ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';
import { boxShadowMixins, mixins } from '@styles/mixins';
import { getCrossfadeImages } from '@util/product';
import { getPaceDescription, getPaceLabel } from '@util/tours';
import { Icons } from '@model/common/icons';
import { Amount, formatAmount, Image, Price, RoundingDirection, TripTypes } from '@model/common';
import { TourResult } from '@model/tours';
import { setLeadInTourReferences } from '@state/slices/tours/tour-references';
import { SignalIcon } from '@components/tours';
import { TourLink, TourProductLink } from '@components/common/next-link/tour-link';
import { HeroGallery } from '@components/common/hero-gallery/HeroGallery';
import { Icon } from '@components/common/generic-icon';
import { ToolTip } from '@components/common/tooltip';
import { TileDiscountBanner } from '@styles/common';

/* ***************** *
 *       Types       *
 * ***************** */
export interface TourDealFinderTileProps {
  testId?: string;
  tour: TourResult;
  tripType: TripTypes;
  query?: ParsedUrlQuery;
  searchToken?: string;
}

/* ***************** *
 *       Styles      *
 * ***************** */

export const Container: any = styled.div(({ theme }: ThemeProps) => ({
  boxShadow: boxShadowMixins(theme.custom.colors.group10.base).default,
  borderRadius: theme.custom.spacing.small,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  height: '100%',
  color: theme.custom.colors.group4.base,
  cursor: 'pointer',
  ':hover': {
    boxShadow: boxShadowMixins(theme.custom.colors.group10.base).heavy
  }
}));

export const ContentContainer: any = styled.div({
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1
});

export const IntroContainer = styled.div(({ theme }: ThemeProps) => ({
  padding: theme.custom.spacing.medium,
  borderBottom: `1px solid ${theme.custom.colors.group10.light}`,
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1
}));

export const DestinationLabel = styled.span(({ theme }: ThemeProps) => ({
  display: 'block',
  textTransform: 'uppercase',
  ...(theme.custom.typography.labels.small as any),
  fontWeight: 500,
  ...mixins(theme).truncate
}));

export const TourNameLabel = styled.span(({ theme }: ThemeProps) => ({
  display: 'block',
  marginBottom: theme.custom.spacing.xSmall,
  lineHeight: '2.6rem',
  marginTop: theme.custom.spacing.xSmall,
  ...(theme.custom.typography.h5 as any),
  fontWeight: 500
}));

export const SubTitle = styled.p({
  marginTop: 'auto'
});

export const AttributesContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between'
});

interface AttributeProps extends ThemeProps {
  noDisplay?: boolean;
}

export const Attribute: any = styled.div(({ theme, noDisplay }: AttributeProps) => ({
  paddingRight: theme.custom.spacing.xSmall,
  marginTop: theme.custom.spacing.xSmall,
  visibility: noDisplay ? 'hidden' : 'visible'
}));

export const AttributeIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: '1.5rem',
  color: theme.custom.colors.group4.base
}));

export const AttributeLabel = styled.span(({ theme }: ThemeProps) => ({
  paddingLeft: theme.custom.spacing.xSmall,
  whiteSpace: 'nowrap',
  ...(theme.custom.typography.labels.default as any),
  color: theme.custom.colors.group4.light
}));

export const PriceRow: any = styled.div(({ noDisplay }: { noDisplay?: boolean }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  visibility: noDisplay ? 'hidden' : 'visible'
}));

export const PricingContainer = styled.div(({ theme }: ThemeProps) => ({
  padding: theme.custom.spacing.medium,
  [PriceRow]: {
    marginBottom: theme.custom.spacing.small,
    '&:last-of-type': {
      marginBottom: 0
    }
  }
}));

export const TourLength = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.default as any)
}));

export const BoldPrice = styled.h5(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  fontWeight: 'bold',
  margin: 0,
  display: 'inline-block',
  marginRight: 2
}));

export const PerPerson = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.default as any)
}));

export const PriceContainer = styled.div({
  whiteSpace: 'nowrap'
});

export const PaceDescription = styled.div({
  maxWidth: 500
});

/* ****************************** *
 *    TourProductTileComponent    *
 * ***************************** */
export interface TourProductTileComponentProps {
  path: string;
  tripType: TripTypes;
  testId?: string;
  handleClick?: () => void;
  query?: ParsedUrlQuery;
  tourName: string;
  price?: Price;
  discount?: number;
  featuredImage: Image;
  gallery: Array<Image>;
  country: string;
  continent?: string;
  subTitle: string;
  mealsCount?: number;
  excursionsCount?: number;
  pace?: number;
  pageLink?: boolean;
}

export const TourProductTileComponent = ({
  path,
  tripType,
  testId,
  handleClick,
  query,
  tourName,
  price,
  discount,
  featuredImage,
  gallery,
  country,
  continent,
  subTitle,
  mealsCount,
  excursionsCount,
  pace,
  pageLink
}: TourProductTileComponentProps) => {
  const t: any = useI18NextContext();
  const LinkComponent = pageLink ? TourLink : TourProductLink;
  return (
    <LinkComponent onClick={handleClick} path={path} tripType={tripType} query={query}>
      <Container data-testid={testId || TestId.tourProductTile.main}>
        {price && discount && (
          <TileDiscountBanner>
            {t('tour-product__discount--label-tour-tile', {
              discount: formatAmount(discount, price.currency)
            })}
          </TileDiscountBanner>
        )}
        <HeroGallery images={getCrossfadeImages(featuredImage, gallery)} isTile />
        <ContentContainer data-testid={TestId.dealFinder.tile.contentContainer}>
          <IntroContainer>
            <DestinationLabel data-testid={TestId.dealFinder.tile.destination}>
              {country}
              {continent && `, ${continent}`}
            </DestinationLabel>
            <TourNameLabel data-testid={TestId.dealFinder.tile.tourName}>{tourName}</TourNameLabel>
            <SubTitle>{subTitle}</SubTitle>
            <AttributesContainer>
              {!!mealsCount && (
                <Attribute>
                  <AttributeIcon name={Icons.MEAL} />
                  <AttributeLabel data-testid={TestId.dealFinder.tile.nights}>
                    {mealsCount} {t('common__label--meals')}
                  </AttributeLabel>
                </Attribute>
              )}
              {!!excursionsCount && (
                <Attribute>
                  <AttributeIcon name={Icons.EYE} />
                  <AttributeLabel data-testid={TestId.dealFinder.tile.nights}>
                    {excursionsCount} {t('common__label--excursions')}
                  </AttributeLabel>
                </Attribute>
              )}
              {!!pace && (
                <Attribute>
                  <ToolTip content={<PaceDescription>{t(getPaceDescription(pace))}</PaceDescription>} distance={10}>
                    <>
                      <SignalIcon pace={pace} />
                      <AttributeLabel data-testid={TestId.dealFinder.tile.nights}>
                        {t(getPaceLabel(pace))}
                      </AttributeLabel>
                    </>
                  </ToolTip>
                </Attribute>
              )}
            </AttributesContainer>
          </IntroContainer>
          <PricingContainer>
            <PriceRow data-testid={TestId.dealFinder.tile.priceRow} noDisplay={!price?.amount}>
              <TourLength data-testid={TestId.dealFinder.tile.mealPlan}>{t('common__label--prices-from')}</TourLength>
              <PriceContainer>
                <BoldPrice data-testid={TestId.dealFinder.tile.leadInPrice}>
                  {new Amount(price?.amount, price?.currency?.code).round(RoundingDirection.UP).format()}
                </BoldPrice>
                <PerPerson>{t('common__label--per-person-short')}</PerPerson>
              </PriceContainer>
            </PriceRow>
          </PricingContainer>
        </ContentContainer>
      </Container>
    </LinkComponent>
  );
};

export const TourDealFinderTile = ({ testId, tour, tripType, query, searchToken }: TourDealFinderTileProps) => {
  const dispatch = useDispatch();

  const { summary, leadInPrice, information, tripType: tourTripType } = tour;
  const { featuredImage, gallery, place, subTitle } = summary;
  const { excursions, meals, pace } = information;
  const { perPerson, discount, references, airport, date } = leadInPrice;

  const handleTourClick = () => {
    dispatch(setLeadInTourReferences({ packageReferences: references }));
  };

  const tileProps: TourProductTileComponentProps = {
    path: place.tour?.name.path || '',
    tripType: tourTripType || tripType,
    testId,
    handleClick: handleTourClick,
    query: { ...query, searchToken, airports: airport, departureDate: date.split('T')[0] },
    tourName: place.tour?.name.display || '',
    price: perPerson,
    discount: discount?.amount.amount,
    featuredImage,
    gallery,
    country: place?.country.name.display,
    continent: place?.continent.display,
    subTitle,
    mealsCount: meals,
    excursionsCount: excursions,
    pace
  };
  return <TourProductTileComponent {...tileProps} />;
};
